import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useDialogStore } from '../../stores/dialogStore';
import ProfileImageGallery from './ProfileImageGallery';

const ProfileDetails = ({ profileId }) => {
  const { accessToken, profile: currentUserProfile } = useAuthStore();
  const { confirm } = useDialogStore();
  const [profileData, setProfileData] = useState(null);
  const [detailTypes, setDetailTypes] = useState([]);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingValues, setEditingValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});

  // Use provided profileId or fall back to current user's profile
  const targetProfileId = profileId || currentUserProfile?.id;

  useEffect(() => {
    if (!targetProfileId) return;

    fetchProfileDetails();
  }, [targetProfileId, accessToken]);

  // Ensure all detail types have at least one input field
  useEffect(() => {
    if (detailTypes.length === 0) return;

    setEditingValues(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      detailTypes.forEach(type => {
        if (!updated[type.id] || updated[type.id].length === 0) {
          updated[type.id] = [{ id: null, value: '', notes: '' }];
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [detailTypes]);

  const fetchProfileDetails = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/profile/details/${targetProfileId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setProfileData(data.profile);
        setDetailTypes(data.detail_types);
        setIsOwnProfile(data.is_own_profile);

        // Initialize editing values
        const initialValues = {};
        data.detail_types.forEach(type => {
          initialValues[type.id] = type.values.map(v => ({
            id: v.id,
            value: getValueFromDetail(v, type.datatype_code),
            notes: v.notes || ''
          }));

          // Ensure at least one empty value for ALL fields (system and custom)
          if (initialValues[type.id].length === 0) {
            initialValues[type.id] = [{ id: null, value: '', notes: '' }];
          }
        });
        setEditingValues(initialValues);
        setOriginalValues(JSON.parse(JSON.stringify(initialValues))); // Deep copy
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load profile details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getValueFromDetail = (detail, datatypeCode) => {
    switch (datatypeCode) {
      case 'NUMBER':
        return detail.number_value !== null ? Math.round(detail.number_value) : null;
      case 'TEXT':
      case 'TEXTAREA':
        return detail.text_value;
      case 'BOOLEAN':
        return detail.bool_value;
      case 'SELECT':
        return detail.option_id;
      case 'DATE':
        return detail.date_value;
      default:
        return '';
    }
  };

  const handleValueChange = (typeId, valueIndex, newValue) => {
    setEditingValues(prev => ({
      ...prev,
      [typeId]: prev[typeId].map((v, idx) =>
        idx === valueIndex ? { ...v, value: newValue } : v
      )
    }));
  };

  const handleAddValue = (typeId) => {
    setEditingValues(prev => ({
      ...prev,
      [typeId]: [...(prev[typeId] || []), { id: null, value: '', notes: '' }]
    }));
  };

  const handleRemoveValue = (typeId, valueIndex) => {
    setEditingValues(prev => ({
      ...prev,
      [typeId]: prev[typeId].filter((_, idx) => idx !== valueIndex)
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setOriginalValues(JSON.parse(JSON.stringify(editingValues))); // Backup current values
  };

  const handleCancel = () => {
    setEditingValues(JSON.parse(JSON.stringify(originalValues))); // Restore backup
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async (skipRefresh = false, exitEditMode = true) => {
    try {
      // Format data for API
      const details = Object.entries(editingValues).map(([typeId, values]) => ({
        type_id: parseInt(typeId),
        values: values.filter(v => v.value !== '' && v.value !== null).map(v => ({
          value: v.value,
          notes: v.notes
        }))
      }));

      const response = await fetch(`/api/profile/details/${targetProfileId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ details })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        if (!skipRefresh) {
          setError(null);
          setSuccessMessage('Profile saved successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
          await fetchProfileDetails();
          if (exitEditMode) {
            setIsEditing(false);
          }
        }
        return true;
      } else {
        setSuccessMessage(null);
        setError(data.message);
        return false;
      }
    } catch (err) {
      setSuccessMessage(null);
      setError('Failed to save profile details');
      console.error(err);
      return false;
    }
  };

  const handleAddDetailType = async (label, datatype, allowMultiple) => {
    try {
      // First, save current unsaved changes (if in edit mode)
      if (isEditing) {
        const saved = await handleSave(true, false); // skip refresh, don't exit edit mode
        if (!saved) {
          setError('Please fix errors before adding a new field');
          return;
        }
      }

      // Now add the new detail type
      const response = await fetch('/api/profile/detail-types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          label,
          datatype,
          allow_multiple: allowMultiple ? 1 : 0
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowAddModal(false);
        setError(null);
        setSuccessMessage('Custom field added successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        // Now refresh to get the new field
        await fetchProfileDetails();
      } else {
        setSuccessMessage(null);
        setError(data.message);
      }
    } catch (err) {
      setSuccessMessage(null);
      setError('Failed to create detail type');
      console.error(err);
    }
  };

  const handleDeleteDetailType = async (typeId) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this detail type? All associated values will be removed.',
      'Delete Detail Type',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      // First, save current unsaved changes (if in edit mode)
      if (isEditing) {
        const saved = await handleSave(true, false); // skip refresh, don't exit edit mode
        if (!saved) {
          setError('Please fix errors before deleting this field');
          return;
        }
      }

      // Now delete the detail type
      const response = await fetch(`/api/profile/detail-types/${typeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setError(null);
        setSuccessMessage('Custom field deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchProfileDetails();
      } else {
        setSuccessMessage(null);
        setError(data.message);
      }
    } catch (err) {
      setSuccessMessage(null);
      setError('Failed to delete detail type');
      console.error(err);
    }
  };

  const renderFieldValue = (type, value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-secondary italic">Not set</span>;
    }

    switch (type.datatype_code) {
      case 'NUMBER':
        // Format number with 0 decimal places
        const formattedNumber = Math.round(parseFloat(value));
        return (
          <span className="text-primary">
            {formattedNumber} {type.unit_label && <span className="text-sm text-secondary ml-1">{type.unit_label}</span>}
          </span>
        );
      case 'TEXT':
        return <span className="text-primary">{value}</span>;
      case 'TEXTAREA':
        return (
          <div className="text-primary whitespace-pre-wrap">
            {value}
          </div>
        );
      case 'BOOLEAN':
        return <span className="text-primary">{value === 1 || value === true ? 'Yes' : 'No'}</span>;
      case 'SELECT':
        const option = type.options.find(opt => opt.id === value);
        return <span className="text-primary">{option?.label || 'Unknown'}</span>;
      case 'DATE':
        // Format date from yyyy-MM-dd to MM/dd/yyyy
        const dateParts = value.split('-');
        if (dateParts.length === 3) {
          const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
          return <span className="text-primary">{formattedDate}</span>;
        }
        return <span className="text-primary">{value}</span>;
      default:
        return <span className="text-primary">{value}</span>;
    }
  };

  const renderFieldInput = (type, valueIndex) => {
    const value = editingValues[type.id]?.[valueIndex]?.value ?? '';

    switch (type.datatype_code) {
      case 'NUMBER':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="1"
              value={value !== null && value !== '' ? Math.round(value) : ''}
              onChange={(e) => handleValueChange(type.id, valueIndex, e.target.value ? parseInt(e.target.value) : null)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
              disabled={!isOwnProfile}
            />
            {type.unit_label && (
              <span className="text-sm text-secondary">{type.unit_label}</span>
            )}
          </div>
        );

      case 'TEXT':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleValueChange(type.id, valueIndex, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
            disabled={!isOwnProfile}
          />
        );

      case 'TEXTAREA':
        return (
          <textarea
            value={value}
            onChange={(e) => handleValueChange(type.id, valueIndex, e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary resize-y"
            disabled={!isOwnProfile}
          />
        );

      case 'BOOLEAN':
        return (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value === 1 || value === true}
              onChange={(e) => handleValueChange(type.id, valueIndex, e.target.checked ? 1 : 0)}
              className="w-4 h-4"
              disabled={!isOwnProfile}
            />
            <span className="text-sm text-secondary">Yes</span>
          </label>
        );

      case 'SELECT':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleValueChange(type.id, valueIndex, parseInt(e.target.value) || null)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
            disabled={!isOwnProfile}
          >
            <option value="">Select...</option>
            {type.options.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        );

      case 'DATE':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleValueChange(type.id, valueIndex, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
            disabled={!isOwnProfile}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 rounded text-green-700 dark:text-green-200">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 rounded text-red-700 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Profile Image Gallery */}
      <ProfileImageGallery profileId={targetProfileId} isOwnProfile={isOwnProfile} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">{profileData?.display_name}</h2>
          <p className="text-sm text-secondary">
            {profileData?.role_id === 1 ? 'Master' : 'slave'}
          </p>
        </div>

        {/* Action buttons - only show for own profile */}
        {isOwnProfile && (
          <div className="flex gap-2">
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="p-2 text-secondary hover:text-primary transition-colors"
                title="Edit profile"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave()}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Detail Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {detailTypes.map(type => (
          <div key={type.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 italic">
                {type.label}
                {!type.is_custom && <span className="text-red-500 ml-1">*</span>}
              </label>
              {isOwnProfile && isEditing && type.is_custom && (
                <button
                  onClick={() => handleDeleteDetailType(type.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                  title="Delete this field"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Render values - View or Edit mode */}
            {isEditing && isOwnProfile ? (
              /* EDIT MODE */
              <div className="space-y-2">
                {(editingValues[type.id] || []).map((_, valueIndex) => (
                  <div key={valueIndex} className="flex items-center gap-2">
                    <div className="flex-1">
                      {renderFieldInput(type, valueIndex)}
                    </div>
                    {type.allow_multiple && editingValues[type.id].length > 1 && (
                      <button
                        onClick={() => handleRemoveValue(type.id, valueIndex)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}

                {/* Add button for multiple values */}
                {type.allow_multiple && (
                  <button
                    onClick={() => handleAddValue(type.id)}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add another
                  </button>
                )}
              </div>
            ) : (
              /* VIEW MODE */
              <div className="space-y-1">
                {(editingValues[type.id] || []).map((valueData, valueIndex) => (
                  <div key={valueIndex}>
                    {renderFieldValue(type, valueData.value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Custom Field Button - only show in edit mode */}
      {isOwnProfile && isEditing && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-secondary hover:border-primary-600 dark:hover:border-primary-400 hover:text-primary transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Custom Field
          </button>
        </div>
      )}

      {/* Add Custom Field Modal */}
      {showAddModal && (
        <AddDetailTypeModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddDetailType}
        />
      )}
    </div>
  );
};

// Modal component for adding custom detail types
const AddDetailTypeModal = ({ onClose, onAdd }) => {
  const [label, setLabel] = useState('');
  const [datatype, setDatatype] = useState('TEXT');
  const [allowMultiple, setAllowMultiple] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (label.trim()) {
      onAdd(label.trim(), datatype, allowMultiple);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-primary mb-4">Add Custom Field</h3>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Field Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                placeholder="e.g., Favorite Color"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Field Type</label>
              <select
                value={datatype}
                onChange={(e) => setDatatype(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
              >
                <option value="TEXT">Text</option>
                <option value="TEXTAREA">Text Area</option>
                <option value="NUMBER">Number</option>
                <option value="DATE">Date</option>
                <option value="BOOLEAN">Yes/No</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowMultiple}
                  onChange={(e) => setAllowMultiple(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-primary">Allow multiple values</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
            >
              Add Field
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileDetails;
